import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Hotel, Settings, MapPin, Plane, Calendar, Clock, CheckCircle, RefreshCw, ExternalLink } from 'lucide-react';

const getAdminToken = () =>
  localStorage.getItem('adminToken') ||
  localStorage.getItem('userToken') ||
  localStorage.getItem('jwt') || '';

const AdminHotels = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingId, setMarkingId] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const fetchPending = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/pending-scraping-requests', {
        headers: { Authorization: `Bearer ${getAdminToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPending(data.pending_requests || []);
    } catch (err) {
      setError(`Failed to load pending requests: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const markActive = async (item) => {
    setMarkingId(item.id);
    try {
      const res = await fetch(`/api/admin/competitor-hotels/${item.id}/mark-active`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${getAdminToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSuccessMsg(`"${item.ota_hotel_name}" marked as active.`);
      setTimeout(() => setSuccessMsg(null), 3000);
      setPending((prev) => prev.filter((p) => p.id !== item.id));
    } catch (err) {
      setError(`Failed to mark active: ${err.message}`);
    } finally {
      setMarkingId(null);
    }
  };

  useEffect(() => { fetchPending(); }, []);

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Hotel Configuration</h1>
          <p className="text-muted-foreground">Manage pending PromptCloud scraping requests from clients</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchPending} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Notifications */}
      {successMsg && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
          {successMsg}
        </div>
      )}
      {error && (
        <div className="flex items-center justify-between bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-4 shrink-0">✕</button>
        </div>
      )}

      {/* Pending scraping requests */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-500" />
            Pending Scraping Requests
            {pending.length > 0 && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-300">{pending.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Competitor hotels added by clients that need to be configured in PromptCloud before pricing data will appear.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
          ) : pending.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center border border-dashed rounded-lg flex flex-col items-center gap-2">
              <CheckCircle className="h-8 w-8 text-green-400" />
              All caught up — no pending scraping requests.
            </div>
          ) : (
            <div className="space-y-3">
              {pending.map((item) => (
                <div key={item.id} className="flex items-start justify-between p-4 border rounded-lg bg-amber-50/40 gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm">{item.ota_hotel_name}</p>
                      <Badge variant="outline" className="text-xs capitalize">{item.ota}</Badge>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                      <span><strong>Client:</strong> {item.client_name || '—'}</span>
                      <span><strong>Property:</strong> {item.property_name}{item.property_city ? `, ${item.property_city}` : ''}</span>
                      <span><strong>Requested:</strong> {fmt(item.created_at)}</span>
                    </div>
                    {item.hotel_url && (
                      <a
                        href={item.hotel_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {item.hotel_url.length > 60 ? item.hotel_url.slice(0, 60) + '…' : item.hotel_url}
                      </a>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => markActive(item)}
                    disabled={markingId === item.id}
                    className="shrink-0 text-green-700 border-green-300 hover:bg-green-50"
                  >
                    {markingId === item.id ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <><CheckCircle className="h-4 w-4 mr-1" /> Mark Active</>
                    )}
                  </Button>
                </div>
              ))}
              <p className="text-xs text-muted-foreground pt-1">
                To action: open the OTA URL, add it to PromptCloud's crawl config, then click <strong>Mark Active</strong>.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Requests</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '…' : pending.length}</div>
            <p className="text-xs text-muted-foreground">Awaiting PromptCloud setup</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">PromptCloud Feed ID</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-mono font-bold">inper_109ec46926</div>
            <p className="text-xs text-muted-foreground">Data feed identifier</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">PromptCloud Portal</CardTitle>
            <Hotel className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <a
              href="https://app.promptcloud.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
            >
              <ExternalLink className="h-3 w-3" /> app.promptcloud.com
            </a>
            <p className="text-xs text-muted-foreground mt-1">Add new URLs here</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminHotels;
